import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaUser, FaCalendar, FaMapMarker, FaRuler, FaWeight, FaPalette, FaClock, FaStar } from 'react-icons/fa';
import { registerAPI } from '../services/api';

const UserDetails = () => {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        fetchUserDetails();
    }, [id]);
    
    const fetchUserDetails = async () => {
        setLoading(true);
        try {
            const response = await registerAPI.getUserById(id);
            if (response.data.success) {
                setUser(response.data.data);
            }
        } catch (error) {
            toast.error('Error fetching user details');
        } finally {
            setLoading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <div className="inline-block w-12 h-12 border-t-2 border-indigo-600 rounded-full animate-spin"></div>
                    <p className="mt-2 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!user) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600">User not found</p>
                <Link to="/users" className="btn-primary mt-4 inline-block">Back to Users</Link>
            </div>
        );
    }
    
    // Helper function to get country name from ID
    const getCountryName = (countryId) => {
        const countries = {
            '153': 'Nepal',
            '1': 'USA',
            '91': 'India',
            '44': 'UK',
            '61': 'Australia'
        };
        return countries[countryId] || countryId || 'Not specified';
    };
    
    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <Link to="/users" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-4">
                    <FaArrowLeft className="mr-2" />
                    Back to Users
                </Link>
                
                <div className="card">
                    <div className="p-6 md:p-8">
                        {/* Profile Header */}
                        <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 mb-8">
                            {user.personal.image && (
                                <img
                                    src={`https://app.bihebari.com.np/uploads/${user.personal.image}`}
                                    alt={user.personal.name}
                                    className="w-32 h-32 rounded-full object-cover border-4 border-indigo-300"
                                />
                            )}
                            <div className="text-center md:text-left">
                                <h1 className="text-3xl font-bold text-gray-800">{user.personal.name}</h1>
                                <p className="text-gray-600 mt-1">{user.personal.gender} • {user.personal.marital}</p>
                                <p className="text-gray-500 text-sm mt-2">{user.personal.about_me}</p>
                            </div>
                        </div>
                        
                        {/* Personal Details */}
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <FaUser className="mr-2 text-indigo-600" />
                                Personal Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center"><FaCalendar className="mr-2 text-gray-500" /> <strong className="w-32">Date of Birth:</strong> {user.personal.dob}</div>
                                <div className="flex items-center"><FaClock className="mr-2 text-gray-500" /> <strong className="w-32">Birth Time:</strong> {user.personal.birth_time || 'Not specified'}</div>
                                <div className="flex items-center"><FaMapMarker className="mr-2 text-gray-500" /> <strong className="w-32">Birth Country:</strong> {getCountryName(user.personal.birth_location) || 'Not specified'}</div>
                                <div className="flex items-center"><FaMapMarker className="mr-2 text-gray-500" /> <strong className="w-32">Birth State:</strong> {user.personal.birth_state || 'Not specified'}</div>
                                <div className="flex items-center"><FaMapMarker className="mr-2 text-gray-500" /> <strong className="w-32">Birth City:</strong> {user.personal.birth_city || 'Not specified'}</div>
                                <div className="flex items-center"><FaStar className="mr-2 text-gray-500" /> <strong className="w-32">Horoscope/Rashi:</strong> {user.personal.horscope || 'Not specified'}</div>
                                <div className="flex items-center"><strong className="w-32">Caste:</strong> {user.personal.caste || 'Not specified'}</div>
                                <div className="flex items-center"><strong className="w-32">Gotra:</strong> {user.personal.gotra || 'Not specified'}</div>
                                <div className="flex items-center"><strong className="w-32">Mother's Surname:</strong> {user.personal.moms_surname || 'Not specified'}</div>
                                <div className="flex items-center"><strong className="w-32">Children:</strong> {user.personal.children || 0}</div>
                                <div className="flex items-center"><strong className="w-32">Children Status:</strong> {user.personal.children_status || 'Not specified'}</div>
                                <div className="flex items-center"><strong className="w-32">Food Preference:</strong> {user.personal.eat_status}</div>
                                <div className="flex items-center"><strong className="w-32">Payment Status:</strong> 
                                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                        user.personal.pay_status === 'paid' ? 'bg-green-100 text-green-800' :
                                        user.personal.pay_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {user.personal.pay_status}
                                    </span>
                                </div>
                                {user.personal.amount && <div className="flex items-center"><strong className="w-32">Amount:</strong> ${user.personal.amount}</div>}
                            </div>
                        </div>
                        
                        {/* Appearance Details */}
                        {user.appearance && (
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                    <FaRuler className="mr-2 text-indigo-600" />
                                    Appearance Details
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center"><FaRuler className="mr-2 text-gray-500" /> Height: {user.appearance.height} cm</div>
                                    <div className="flex items-center"><FaWeight className="mr-2 text-gray-500" /> Weight: {user.appearance.weight} kg</div>
                                    <div className="flex items-center"><FaPalette className="mr-2 text-gray-500" /> Skin Tone: {user.appearance.skin_tone}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;